/**
 * /payroll/pay-runs — Pay Run Queue
 * Tabs: Active | All | Voided
 * "New Pay Run" button → dialog
 */
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { api } from "@/lib/api";
import { payRunsQueryOptions, payrollKeys } from "@/lib/queries";
import type { PayRun, PayRunStatus } from "@/lib/types";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll/pay-runs")({
  component: PayRunsPage,
});

const STATUS_COLOR: Record<PayRunStatus, "default" | "primary" | "warning" | "success" | "error" | "info"> = {
  draft: "default",
  calculated: "primary",
  approved: "success",
  paid: "info",
  posted: "success",
  voided: "error",
};
const STATUS_LABEL: Record<PayRunStatus, string> = {
  draft: "Draft", calculated: "Calculated", approved: "Approved",
  paid: "Paid", posted: "Posted", voided: "Voided",
};

function fmtMoney(v: string | number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}
function fmtDate(s: string) {
  return new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

// ─── New Pay Run dialog ───────────────────────────────────────────────────────

interface NewPayRunDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

function NewPayRunDialog({ open, onClose, onCreated }: NewPayRunDialogProps) {
  const qc = useQueryClient();
  const today = new Date().toISOString().split("T")[0];
  const [form, setForm] = useState({
    period_start: "",
    period_end: "",
    pay_date: today,
    pay_frequency: "biweekly",
    notes: "",
  });

  const mut = useMutation({
    mutationFn: (body: typeof form) =>
      api<PayRun>("/api/payroll/pay-runs", { method: "POST", body }),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: payrollKeys.payRuns() });
      onCreated(data.id);
    },
  });

  const valid = form.period_start && form.period_end && form.pay_date;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Pay Run</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          <TextField label="Period Start" type="date" value={form.period_start}
            onChange={(e) => setForm(f => ({ ...f, period_start: e.target.value }))}
            InputLabelProps={{ shrink: true }} required />
          <TextField label="Period End" type="date" value={form.period_end}
            onChange={(e) => setForm(f => ({ ...f, period_end: e.target.value }))}
            InputLabelProps={{ shrink: true }} required />
        </Box>
        <TextField label="Pay Date" type="date" value={form.pay_date}
          onChange={(e) => setForm(f => ({ ...f, pay_date: e.target.value }))}
          InputLabelProps={{ shrink: true }} required />
        <FormControl>
          <InputLabel>Pay Frequency</InputLabel>
          <Select value={form.pay_frequency} label="Pay Frequency"
            onChange={(e) => setForm(f => ({ ...f, pay_frequency: e.target.value }))}>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="biweekly">Bi-weekly</MenuItem>
            <MenuItem value="semi_monthly">Semi-monthly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
          </Select>
        </FormControl>
        <TextField label="Notes (optional)" value={form.notes} multiline rows={2}
          onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))} />
        {mut.isError && (
          <Alert severity="error">{String((mut.error as Error)?.message ?? "Error creating pay run")}</Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => mut.mutate(form)} disabled={mut.isPending || !valid}>
          {mut.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

function PayRunsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);

  const statusFilter = tab === 2 ? "voided" : undefined;
  const { data, isLoading } = useQuery(payRunsQueryOptions(statusFilter));

  // Client-side filter for "Active" tab (exclude voided/posted)
  const rows = (data ?? []).filter((r: PayRun) => {
    if (tab === 0) return !["voided", "posted"].includes(r.status);
    if (tab === 2) return r.status === "voided";
    return true;
  });

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={600}>Pay Runs</Typography>
          <Box sx={{ mt: 0.5 }}>
            <HelpHint docPath="payroll-l1/pay-run-lifecycle.md" label="Pay run help" />
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
          New Pay Run
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Active" />
        <Tab label="All" />
        <Tab label="Voided" />
      </Tabs>

      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>
      ) : (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Run #</TableCell>
              <TableCell>Period</TableCell>
              <TableCell>Pay Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="right">Gross</TableCell>
              <TableCell align="right">EE Taxes</TableCell>
              <TableCell align="right">Net Pay</TableCell>
              <TableCell align="right">ER Cost</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((run: PayRun) => (
              <TableRow
                key={run.id} hover sx={{ cursor: "pointer" }}
                onClick={() => navigate({ to: "/payroll/pay-runs/$payRunId", params: { payRunId: run.id } })}
              >
                <TableCell sx={{ fontWeight: 500 }}>{run.pay_run_number}</TableCell>
                <TableCell>
                  {fmtDate(run.period_start)} – {fmtDate(run.period_end)}
                </TableCell>
                <TableCell>{fmtDate(run.pay_date)}</TableCell>
                <TableCell align="center">
                  <Chip label={STATUS_LABEL[run.status as PayRunStatus]} color={STATUS_COLOR[run.status as PayRunStatus]} size="small" />
                </TableCell>
                <TableCell align="right">{fmtMoney(run.total_gross)}</TableCell>
                <TableCell align="right">{fmtMoney(run.total_taxes)}</TableCell>
                <TableCell align="right">{fmtMoney(run.total_net)}</TableCell>
                <TableCell align="right">{fmtMoney(run.total_employer_costs)}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: "text.secondary" }}>
                  No pay runs found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}

      <NewPayRunDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        onCreated={(id) => { setDialogOpen(false); navigate({ to: "/payroll/pay-runs/$payRunId", params: { payRunId: id } }); }}
      />
    </Box>
  );
}
